import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    forwardRef,
    HostBinding,
    HostListener,
    inject,
    Inject,
    Optional,
    output,
    SkipSelf,
    viewChild
} from "@angular/core";
import { FormWrapper, VContainerComponent } from "../wrapper.interface";
import { ControlContainer, NG_VALUE_ACCESSOR, ReactiveFormsModule } from "@angular/forms";
import { CommonModule } from "@angular/common";
import { V_COMPONENT_CONTAINER, V_FORM_CONTROL_TOKEN, WRAPPER_TOKEN } from "../wrapper.tokens";
import { AppService } from "src/services/app.service";
import { TranslateModule } from "@ngx-translate/core";
import { GcLayoutModule } from "src/app/components/v-layout/layout.module";
import { UploadFile, Value } from "../fileUpload/interfaces";
import { FileUploadPickerConfig } from "./fileUpload-picker.config";

@Component({
    selector:    'v-fileUpload-picker',
    templateUrl: './fileUpload-picker.component.haml',
    styleUrls:   ['./fileUpload-picker.component.sass', '../input.style.sass'],
    providers:   [
        { provide: V_FORM_CONTROL_TOKEN, useExisting: forwardRef(() => VFileUploadPickerComponent) },
        { provide: WRAPPER_TOKEN, useExisting: forwardRef(() => VFileUploadPickerComponent) },
        { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => VFileUploadPickerComponent), multi: true }
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports:         [CommonModule, ReactiveFormsModule, TranslateModule, GcLayoutModule]
})
export class VFileUploadPickerComponent<T extends UploadFile>  extends FormWrapper<FileUploadPickerConfig, Value | Value[]> {
    protected readonly _baseConfig: FileUploadPickerConfig = FileUploadPickerConfig.baseTheme.Default;
    protected readonly app = inject(AppService);
    protected readonly onlySingleFileUpload = computed(() => this.config().uploadLimit === 1);
    /**
     * `acceptedFileTypes` is a comma separated MIME list (also used as `accept` on the input).
     * Compare against the parsed entries instead of the raw string, so `''` does not match every
     * file type and a partial MIME type does not pass as a substring.
     */
    protected readonly acceptedFileTypes = computed(() =>
        (this.config().acceptedFileTypes ?? '').split(',').map(type => type.trim()).filter(Boolean));

    @HostBinding('class.drag') dragOver = false;
    fileSelect = output<T>();
    inputElementRef = viewChild.required<HTMLInputElement>('input');

    constructor(
        @Optional() protected controlContainer: ControlContainer,
        @Inject(V_COMPONENT_CONTAINER) @Optional() @SkipSelf() protected parent?: VContainerComponent
    ) {
        super(controlContainer, parent);
        effect(() => {
            const control = this.cControl();
            control.GCType = "file";
        });
    }

    @HostListener('dragover', ['$event'])
    onDragOver(): void {
        this.dragOver = true;
    }

    @HostListener('dragleave', ['$event'])
    onDragLeave(): void {
        this.dragOver = false;
    }

    @HostListener('drop', ['$event'])
    onDrop(): void {
        this.dragOver = false;
    }

    selectFile(event: Event): void {
        const input: HTMLInputElement = <HTMLInputElement>event.target;
        if (!input.files || !input.files[0]) return;
        const result = [];
        for (let i: number = 0; i < input.files.length; i++) {
            const file: File = input.files[i];
            if (!this.acceptedFileTypes().includes(file.type))
                return this.postError(this.fileTypeError(file));
            const sizeInMb: number = file.size / (1024 ** 2);
            if (sizeInMb > this.config().fileSizeLimit)
                return this.postError(`Die Dateigröße der Datei ${file.name} (${sizeInMb} MB) ist nicht zulässig.`);


            const src = URL.createObjectURL(file);
            let fileMeta: T = {
                name: file.name,
                size: file.size,
                src,
            } as T;
            result.push(src);
            if (file.type.includes('image')) {
                const image = new Image();
                image.onload = () => {
                    // TODO: extra properties
                    // result = {
                    //     ...result,
                    //     width:  image.naturalWidth,
                    //     height: image.naturalHeight,
                    // };
                    this.fileSelect.emit(fileMeta);
                };
                image.src = src;
                continue;
            }
            this.fileSelect.emit(fileMeta);
        }
        this.writeValue(this.onlySingleFileUpload() ? result.at(0) : result);
    }

    private fileTypeError(file: File): string {
        const accepted = this.acceptedFileTypes();
        const rejected = `Die Datei ${file.name}${file.type ? ` (${file.type})` : ''}`;
        if (!accepted.length)
            return `${rejected} kann nicht hochgeladen werden, da für dieses Feld keine Dateitypen zugelassen sind.`;
        return `${rejected} hat einen nicht zulässigen Dateityp. Zulässig sind: ${accepted.join(', ')}.`;
    }

    postError(message: string) {
        console.error(message);
        this.app.messageService.add({
            severity: "error",
            summary:  "Es ist ein Fehler aufgetreten!",
            detail:   message,
            life:     3000,
        });
    }
}